import {testIdProps} from '@github-ui/test-id-props'
import {PlusIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useRef, useState} from 'react'

import type {ColumnModel} from '../../models/column-model'
import {usePrefetchIssueFieldsQuery} from '../../state-providers/issue-fields/use-issue-fields-query'
import {useAddFieldModal} from '../../state-providers/modals/use-add-field-modal'
import {AddColumnMenu} from '../add-column-menu'
import styles from './add-column-header.module.css'
import {AddColumnModal} from './add-column-modal'

type AddColumnMenuWithTriggerProps = {
  showAddFieldModal: boolean
  columnMenuTriggerRef: React.RefObject<HTMLButtonElement | null>
  ref?: React.Ref<HTMLButtonElement>
}

const AddColumnMenuWithTrigger = ({ref, showAddFieldModal, columnMenuTriggerRef}: AddColumnMenuWithTriggerProps) => {
  const [openAddColumnMenu, setOpenAddColumnMenu] = useState<boolean>(false)
  const prefetchIssueFields = usePrefetchIssueFieldsQuery()

  const openColumnMenu = useCallback(() => {
    if (showAddFieldModal) return

    setOpenAddColumnMenu(true)
  }, [showAddFieldModal])

  const isDisabledStyle = showAddFieldModal || openAddColumnMenu

  return (
    <>
      <IconButton
        size="small"
        variant="invisible"
        icon={PlusIcon}
        aria-label="Add field"
        ref={ref}
        onClick={openColumnMenu}
        onKeyPress={openColumnMenu}
        {...testIdProps('column-visibility-menu-trigger')}
        className={clsx(styles.IconButton, {
          [styles.DisabledButton]: isDisabledStyle,
        })}
        onMouseEnter={prefetchIssueFields}
        onFocus={prefetchIssueFields}
      />
      <AddColumnMenu open={openAddColumnMenu} setOpen={setOpenAddColumnMenu} anchorRef={columnMenuTriggerRef} />
    </>
  )
}

AddColumnMenuWithTrigger.displayName = 'AddColumnMenu'

export const AddColumnHeader = memo(() => {
  const {setShowAddFieldModal, showAddFieldModal} = useAddFieldModal()
  const columnMenuTriggerRef = useRef<HTMLButtonElement>(null)

  const onColumnSave = useCallback((newCols: Array<ColumnModel>) => {
    // Focus the last column added
    const focusColumn = newCols.at(-1)
    if (!focusColumn) {
      return
    }

    const scrollToAddButton = () => {
      requestAnimationFrame(() => {
        if (columnMenuTriggerRef.current) {
          columnMenuTriggerRef.current.focus()
          columnMenuTriggerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          })
        }
      })
    }

    setTimeout(() => {
      scrollToAddButton()
    }, 10)
  }, [])

  return (
    <>
      <AddColumnMenuWithTrigger
        showAddFieldModal={showAddFieldModal}
        columnMenuTriggerRef={columnMenuTriggerRef}
        ref={columnMenuTriggerRef}
      />

      <AddColumnModal
        isOpen={showAddFieldModal}
        setOpen={setShowAddFieldModal}
        anchorRef={columnMenuTriggerRef}
        onSave={onColumnSave}
      />
    </>
  )
})

AddColumnHeader.displayName = 'AddColumnHeader'
